'use es6';

import SCOPES from 'IntegrationsUISpaceSword/modules/Auth/Scopes';

// Temporary fix: Adding timeout to all bucket endpoint per critsit re:
// elasticsearch slowness
const EXTENDED_TIMEOUT = 60000;
/**
 *
 * @param {number|string} portalId The portalId present in the url path
 * @param {number|string} appId The appId extracted from the url path (if applicable)
 * @param {{[key: string]: any}} query An object mapping query param names to their JSON-parsed values
 * @param {string} pathname The pathname of the current url. Available as a param for unit testing, defaults to the global object
 */
export const makeEarlyRequestEntries = ({
  portalId,
  appId,
  query,
  pathname = document.location.pathname
}) => {
  const ACCOUNTING_PATH_MAPPING = {
    contacts: 'CONTACT',
    products: 'PRODUCT'
  };
  return [{
    key: 'installed-data-sync-apps',
    url: '/sync-bridge/v4/installed-syncs'
  }, {
    key: 'accounting-apps',
    url: '/accountingextension/v3/integrations/app-ids'
  }, {
    regex: /\/integrations-settings\/\d+\/marketable-contacts-settings/,
    key: 'marketable-contact-apps',
    url: '/integrators/marketable/contacts/v3/app-settings'
  }, {
    /* Main dependency of the sfdc navigation container */
    regex: /\/integrations-settings\/\d+\/installed\/salesforce/,
    key: 'sfdc-settings',
    url: '/sfdc/v1/settings'
  }, {
    /* GoToWebinar webinars call */
    regex: /\/integrations-settings\/\d+\/installed\/gotowebinar/,
    key: 'gtw-webinars',
    url: '/gotowebinar/v1/webinars/all',
    timeout: 30000
  }, {
    /* GoToWebinar webinars settings */
    regex: /\/integrations-settings\/\d+\/installed\/gotowebinar/,
    key: 'gtw-settings',
    url: '/gotowebinar/v1/settings',
    timeout: 30000
  }, {
    /* field mappings contacts fetch */
    regex: /\/integrations-settings\/\d+\/installed\/salesforce\/contacts/,
    key: 'sfdc-mappings-initial',
    url: '/sfdc/v1/mappings?hubSpotModel=contact',
    timeout: 30000
  }, {
    /* field mappings deals fetch */
    regex: /\/integrations-settings\/\d+\/installed\/salesforce\/deals/,
    key: 'sfdc-mappings-initial',
    url: '/sfdc/v1/mappings?hubSpotModel=deal',
    timeout: 30000
  }, {
    /* field mappings companies fetch */
    regex: /\/integrations-settings\/\d+\/installed\/salesforce\/companies/,
    key: 'sfdc-mappings-initial',
    url: '/sfdc/v1/mappings?hubSpotModel=company',
    timeout: 30000
  }, {
    regex: /\/integrations-settings\/\d+\/installed\/salesforce\/(activities|timeline)/,
    key: 'sfdc-task-types',
    url: `/sfdc/v1/tasks/${portalId}/types`
  }, {
    /* GSC sites */
    regex: /\/integrations-settings\/\d+\/installed\/google-search-console/,
    key: 'gsc-sites',
    url: '/google-search-console/sites?includeMissing=true',
    timeout: 30000
  }, {
    /* GSC accounts */
    regex: /\/integrations-settings\/\d+\/installed\/google-search-console/,
    key: 'gsc-account',
    url: '/google-search-console/account',
    timeout: 30000
  }, {
    /* Zoom mappings */
    regex: /\/integrations-settings\/\d+\/installed\/zoom\/users|webinars/,
    key: 'zoom-mappings',
    url: '/zoom/v1/user/mapping',
    timeout: 30000
  }, {
    /* MsTeams teams */
    regex: /\/integrations-settings\/\d+\/installed\/microsoft-teams\/teams/,
    key: 'msteams-teams',
    url: `/msteams-integration/v1/teams?portalId=${portalId}`,
    timeout: 30000
  }, {
    /* Eventbrite organizations */
    regex: /\/integrations-settings\/\d+\/installed\/eventbrite/,
    key: 'eventbrite-organizations',
    url: '/eventbrite/v1/organizations',
    timeout: 30000
  }, {
    /* Eventbrite oauth validate */
    regex: /\/integrations-settings\/\d+\/installed\/eventbrite/,
    key: 'eventbrite-oauth-validate',
    url: '/eventbrite/v1/oauth/validate',
    timeout: 30000
  }, {
    /* Salesforce sync health buckets */
    regex: /\/integrations-settings\/\d+\/installed\/salesforce\/health/,
    key: 'sync-error-buckets',
    url: () => {
      const importId = query.importId ? `importId=${query.importId}` : '';
      return `/syncerrors/v2/salesforce/buckets?${importId}`;
    },
    timeout: EXTENDED_TIMEOUT
  }, {
    /* Salesforce sync health buckets archived */
    regex: /\/integrations-settings\/\d+\/installed\/salesforce\/health/,
    key: 'sync-error-buckets-archive',
    url: '/syncerrors/v2/salesforce/buckets?isArchiveRequest=true',
    timeout: EXTENDED_TIMEOUT
  }, {
    /* Salesforce sync health bucket details */
    regex: /\/integrations-settings\/\d+\/installed\/salesforce\/health(?:\/?)\?(?:|.+&)bucket=.*/,
    condition: () => {
      // only execute if rollup key is not included in url
      return !query.rollupKey;
    },
    key: 'sync-error-bucket-details',
    url: function () {
      const bucketType = query.bucket;
      const limit = query.pageSize || 25;
      const offset = query.pageNumber ? limit * (query.pageNumber - 1) : 0;
      return `/syncerrors/v2/salesforce/buckets/${bucketType}?limit=${limit}&offset=${offset}${query.archived ? '&isArchiveRequest=true' : ''}`;
    }(),
    timeout: EXTENDED_TIMEOUT
  }, {
    /* Salesforce sync health bucket details count */
    regex: /\/integrations-settings\/\d+\/installed\/salesforce\/health(?:\/?)\?(?:|.+&)bucket=.*/,
    condition: () => {
      // only execute if rollup key is not included in url
      return !query.rollupKey;
    },
    key: 'sync-error-bucket-details-count',
    url: `/syncerrors/v2/salesforce/buckets/${query.bucket}/count`,
    timeout: EXTENDED_TIMEOUT
  }, {
    /* Salesforce sync health bucket details count (archived) */
    regex: /\/integrations-settings\/\d+\/installed\/salesforce\/health(?:\/?)\?(?:|.+&)bucket=.*/,
    condition: () => {
      // only execute if rollup key is not included in url
      return !query.rollupKey;
    },
    key: 'sync-error-bucket-details-archive-count',
    url: `/syncerrors/v2/salesforce/buckets/${query.bucket}/count?isArchiveRequest=true`,
    timeout: EXTENDED_TIMEOUT
  }, {
    /* Salesforce sync health rollup details */
    regex: /\/integrations-settings\/\d+\/installed\/salesforce\/health(?:\/?)\?(?:|.+&)rollupKey=.*/,
    key: 'sync-error-rollup-details',
    url: `/syncerrors/v2/salesforce/rollups/${query.rollupKey}`,
    timeout: 30000
  }, {
    /* Salesforce sync health affected records  */
    regex: /\/integrations-settings\/\d+\/installed\/salesforce\/health(?:\/?)\?(?:|.+&)rollupKey=.*/,
    key: 'sync-error-affected-records',
    url: `/syncerrors/v2/salesforce/rollups/${query.rollupKey}/affected-records?limit=25&offset=0${query.recordLabel ? `&label=${query.recordLabel}` : ''}`,
    timeout: 30000
  }, {
    /* Salesforce archive usage */
    regex: /\/integrations-settings\/\d+\/installed\/salesforce\/health/,
    key: 'sync-error-usage',
    url: '/syncerrors/v2/archive/usage',
    timeout: 30000
  }, {
    /* Salesforce Metadata API status */
    regex: /\/integrations-settings\/\d+\/installed\/salesforce\/deals/,
    key: 'metadata-api-status',
    url: `/sfdc/v1/portals/${portalId}/sync-info`
  }, {
    /* Ecommerce */
    regex: /\/integrations-settings\/\d+\/ecommerce/,
    key: 'ecomm-stores',
    url: '/extensions/ecomm/v2/ui/stores'
  }, {
    regex: /\/integrations-settings\/\d+\/ecommerce(?:\/?)\?(?:|.+&)storeToken=.*/,
    key: `ecomm-store-details-${query.storeToken}`,
    url: `/extensions/ecomm/v2/ui/stores/${query.storeToken}`
  }, {
    /* Shopify */
    regex: /\/integrations-settings\/\d+\/installed\/shopify/,
    key: 'ecomm-stores',
    url: '/extensions/ecomm/v2/ui/stores'
  }, {
    regex: /\/integrations-settings\/\d+\/installed\/shopify(?:\/?)\?(?:|.+&)storeToken=.*/,
    key: `ecomm-store-details-${query.storeToken}`,
    url: `/extensions/ecomm/v2/ui/stores/${query.storeToken}`
  }, /* AccountingSettings */
  {
    regex: /\/integrations-settings\/\d+\/installed\/accounting/,
    key: 'accounting-accounts',
    url: `/accountingextension/v3/portal-settings/${appId}`
  }, {
    regex: /\/integrations-settings\/\d+\/installed\/accounting\/\d+\/.*\/syncRules/,
    key: 'accounting-sync-rules',
    condition: () => {
      const objectTypeExpr = /\/accounting\/\d+\/(.*)\//.exec(pathname);
      return Boolean(query.accountId && objectTypeExpr && objectTypeExpr.length > 1 && ACCOUNTING_PATH_MAPPING[objectTypeExpr[1].toLowerCase()]);
    },
    url: () => {
      const objectType = /\/accounting\/\d+\/(.*)\//.exec(pathname)[1];
      return `/accountingextension/v3/portal-settings/${appId}/sync/${query.accountId}/${ACCOUNTING_PATH_MAPPING[objectType]}`;
    }
  }];
};
export const makeAppInstallsEntries = scopes => {
  const hasBusinessUnitAccess = scopes.includes(SCOPES.BUSINESS_UNITS_ACCESS);
  return [{
    key: 'installed-apps',
    url: '/appinstalls/v3/app-install/installed-apps-for-ui-new',
    condition: () => !hasBusinessUnitAccess
  }, {
    key: 'installed-apps-with-business-units',
    url: '/appinstalls/v3/app-install/app-associations-for-portal-and-business-unit',
    condition: () => hasBusinessUnitAccess
  }, /* Business Units */
  {
    regex: /\/integrations-settings\/\d+\/installed/,
    key: 'business-units-dropdown',
    url: '/business-units/v1/business-unit-displays/user/dropdown',
    condition: () => hasBusinessUnitAccess
  }];
};
export const makeNativeStatusRequestEntries = appId => {
  return [{
    //ACCOUNTING
    regex: /\/integrations-settings\/\d+\/installed\/accounting/,
    prodId: appId,
    qaId: appId
  }, {
    // EVENTBRITE
    regex: /\/integrations-settings\/\d+\/installed\/eventbrite/,
    prodId: 28564,
    qaId: 1159843
  }, {
    // GOOGLE_CALENDAR
    regex: /\/integrations-settings\/\d+\/installed\/google-calendar/,
    prodId: 35224,
    qaId: 1160472
  }, {
    // GOOGLE_SEARCH_CONSOLE
    regex: /\/integrations-settings\/\d+\/installed\/google-search-console/,
    prodId: 178446,
    qaId: 1206938
  }, {
    // GO_TO_WEBINAR
    regex: /\/integrations-settings\/\d+\/installed\/gotowebinar/,
    prodId: 35161,
    qaId: 1158066
  }, {
    // OFFICE365
    regex: /\/integrations-settings\/\d+\/installed\/office365/,
    prodId: 184945,
    qaId: 1206939
  }, {
    // QUICKBOOKS
    regex: /\/integrations-settings\/\d+\/installed\/quickBooks/,
    prodId: 188574,
    qaId: 1165378
  }, {
    // SLACK
    regex: /\/integrations-settings\/\d+\/installed\/slack/,
    prodId: 60729,
    qaId: 1162214
  }, {
    // UBER_CONFERENCE
    regex: /\/integrations-settings\/\d+\/installed\/uber-conference/,
    prodId: 28662,
    qaId: 1160103
  }, {
    // SALESFORCE
    regex: /\/integrations-settings\/\d+\/installed\/salesforce/,
    prodId: 38577,
    qaId: 1160722
  }, {
    // SURVEY_MONKEY
    regex: /\/integrations-settings\/\d+\/installed\/surveymonkey/,
    prodId: 35186,
    qaId: 1160471
  }, {
    // ZOOM
    regex: /\/integrations-settings\/\d+\/installed\/zoom/,
    prodId: 178192,
    qaId: 1162897
  }, {
    // JIRA
    regex: /\/integrations-settings\/\d+\/installed\/jira/,
    prodId: 205407,
    qaId: 1175189
  }, {
    // GOOGLE_CHAT
    regex: /\/integrations-settings\/\d+\/installed\/google-chat/,
    prodId: 3280929,
    qaId: 9108910
  }];
};